import React, { useCallback, useEffect, useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { findGlobalPostsForPostTemplateInitiated } from '../../../../store/dashboard/dashboard.actions'
import { FindGlobalPostsForPostTemplateRequest } from '@3m5/sm_publish-frontend-api'
import { Autocomplete, Box, Chip, Grid, Paper, Theme } from '@mui/material'
import ButtonComponent from '@professocial/professocialFormComponents/FormComponents/ButtonComponent'
import makeStyles from '@mui/styles/makeStyles/makeStyles'
import KeyboardArrowLeftRounded from '@mui/icons-material/KeyboardArrowLeftRounded'
import DashboardNetworkContentTabs from '../../../User/UserDashboard/components/DashboardNetworkContentTabs'
import { SocialNetworkLicensePermissions } from '@professocial/professocialTypes/types/commonTypes'
import { getLicensePermissions } from '../../../../store/common/common.selectors'
import {
  DeferredPostSource,
  DeferredPostStatus,
  FindGlobalPostsForPostTemplateResponse,
  FindGlobalPostsForPostTemplateSortMode,
  NetworkType
} from '@3m5/sm_publish-frontend-api'
import Pagination from '../../../../shared/components/NavigationComponents/Pagination'
import { getGlobalPosts } from '../../../../store/dashboard/dashboard.selector'
import TextFieldComponent from '@professocial/professocialFormComponents/FormComponents/TextFieldComponent'
import { DropDownElement } from '@professocial/professocialFormComponents/FormComponents/DropDownComponent'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import FacebookDetailsTab from '../components/FacebookDetailsTab'
import InstagramDetailsTab from '../components/InstagramDetailsTab'
import NoDataComponent from '@professocial/professocialFormComponents/FormComponents/NoDataComponent'

export enum GlobalPostsStatus {
  Successful = 'Successful',
  Individual = 'Individual',
  Manual = 'Manual',
  Notified = 'Notified',
  Pending = 'Pending',
  Failed = 'Failed',
  Deleted = 'Deleted',
  NoActiveUsersForPage = 'NoActiveUsersForPage'
}

export interface ColorKeysWithId {
  id: string,
  key: GlobalPostsStatus
}

interface DetailsFilter {
  limit: number;
  offset: number;
  sorting: DropDownElement | undefined;
  status: DropDownElement[] | undefined;
}

export const colorCodes = [
  { key: GlobalPostsStatus.Successful, color: '#A1DF86' },
  { key: GlobalPostsStatus.Individual, color: '#D0EFC3' },
  { key: GlobalPostsStatus.Manual, color: '#a1baff' },
  { key: GlobalPostsStatus.Notified, color: '#FCFCCC' },
  { key: GlobalPostsStatus.Pending, color: '#B2EBF2' },
  { key: GlobalPostsStatus.Failed, color: '#FF7961' },
  { key: GlobalPostsStatus.Deleted, color: '#E0E0E0' },
  { key: GlobalPostsStatus.NoActiveUsersForPage, color: '#FF7961' },
]

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    width: '100%',
    // padding: theme.spacing(4),
    marginBottom: '10px',
  },
  icon: {
    verticalAlign: 'top',
    fill: theme.palette.info.main,
  },
  root: {
    fontFamily: theme.typography.fontFamily,
    fontSize: '1rem',
    fontWeight: 700,
    textTransform: 'inherit',
    paddingTop: '9px',
    width: '100%',
  },
  button: {
    fontFamily: theme.typography.fontFamily,
    fontSize: '16px',
    fontWeight: 700,
    textTransform: 'initial',
    marginRight: '20px',
  },
}))

const EditorDashboardDetails: React.FC = () => {
  const pageSize = 25

  const classes = useStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const history = useHistory()
  const { id } = useParams<{ id: string }>()

  const licensePermissions: SocialNetworkLicensePermissions = useSelector(getLicensePermissions)
  const globalPostsForTemplate: FindGlobalPostsForPostTemplateResponse | undefined = useSelector(getGlobalPosts)

  const dispatchFindGlobalPostsForPostTemplate = useCallback((params: FindGlobalPostsForPostTemplateRequest) => dispatch(findGlobalPostsForPostTemplateInitiated(params)), [dispatch])

  const [page, setPage] = useState<number>(1)
  const [activeTab, setActiveTab] = React.useState<NetworkType>(NetworkType.Facebook)
  const [tabs, setTabs] = React.useState<NetworkType[]>([NetworkType.Facebook, NetworkType.Instagram])
  const [sortData, setSortData] = React.useState<DropDownElement[]>()
  const [statusData, setStatusData] = React.useState<DropDownElement[]>()
  const [coloredArray, setColoredArray] = React.useState<ColorKeysWithId[]>([])
  const [filter, setFilter] = useState<DetailsFilter>({
    limit: 10,
    offset: 0,
    status: undefined,
    sorting: { key: 'AbsoluteReach', value: 'AbsoluteReach' },
  })

  const defaultValues: FindGlobalPostsForPostTemplateRequest = {
    includeDeferredPosts: false,
    includePublishedPosts: false,
    sortMode: FindGlobalPostsForPostTemplateSortMode.AbsoluteReach,
    networkType: activeTab,
    postTemplateId: id,
    deletedPosts: false,
    offset: pageSize * (page - 1),
    pageSize: 25,
  }

  const extractFilterData = (data?: FindGlobalPostsForPostTemplateResponse) => {
    const sorting: DropDownElement[] = []
    const status: DropDownElement[] = []

    if (data) {
      // get status options
      // GlobalPostsStatus.Successful
      data.publishedPosts.findIndex(item => item.source === DeferredPostSource.AutomaticAbo) !== -1 &&
      status.push({
        key: GlobalPostsStatus.Successful,
        value: GlobalPostsStatus.Successful,
        color: colorCodes.find(item => item.key === GlobalPostsStatus.Successful)?.color
      })

      // GlobalPostsStatus.Individual
      if (data.publishedPosts.findIndex(item => item.source === DeferredPostSource.IndividualPost) !== -1 ||
        data.deferredPosts.findIndex(item => item.source === DeferredPostSource.IndividualPost) !== -1) {
        status.push({
          key: GlobalPostsStatus.Individual,
          value: GlobalPostsStatus.Individual,
          color: colorCodes.find(item => item.key === GlobalPostsStatus.Individual)?.color
        })
      }

      // GlobalPostsStatus.Manual
      if (data.publishedPosts.findIndex(item => item.source === DeferredPostSource.ManualAbo) !== -1 ||
        data.deferredPosts.findIndex(item => item.source === DeferredPostSource.ManualAbo) !== -1) {
        status.push({
          key: GlobalPostsStatus.Manual,
          value: GlobalPostsStatus.Manual,
          color: colorCodes.find(item => item.key === GlobalPostsStatus.Manual)?.color
        })
      }

      // GlobalPostsStatus.Notified
      data.deferredPosts.findIndex(item => item.source === DeferredPostSource.IndividualPost && item.status === DeferredPostStatus.AwaitingUserConfirmation) !== -1 &&
      status.push({
        key: GlobalPostsStatus.Notified,
        value: GlobalPostsStatus.Notified,
        color: colorCodes.find(item => item.key === GlobalPostsStatus.Notified)?.color
      })

      // GlobalPostsStatus.Pending
      if (data.deferredPosts.findIndex(item => item.status === DeferredPostStatus.Scheduled) !== -1 ||
        data.deferredPosts.findIndex(item => item.status === DeferredPostStatus.Processing) !== -1) {
        status.push({
          key: GlobalPostsStatus.Pending,
          value: GlobalPostsStatus.Pending,
          color: colorCodes.find(item => item.key === GlobalPostsStatus.Pending)?.color
        })
      }
      // GlobalPostsStatus.Failed
      data.deferredPosts.findIndex(item => item.status === DeferredPostStatus.ErrorWhilePosting) !== -1 &&
      status.push({
        key: GlobalPostsStatus.Failed,
        value: GlobalPostsStatus.Failed,
        color: colorCodes.find(item => item.key === GlobalPostsStatus.Failed)?.color
      })

      // GlobalPostsStatus.NoActiveUsersForPage
      data.deferredPosts.findIndex(item => item.status === DeferredPostStatus.NoActiveUsersForPage) !== -1 &&
      status.push({
        key: GlobalPostsStatus.NoActiveUsersForPage,
        value: GlobalPostsStatus.NoActiveUsersForPage,
        color: colorCodes.find(item => item.key === GlobalPostsStatus.NoActiveUsersForPage)?.color
      })

      // GlobalPostsStatus.Deleted
      data.publishedPosts.findIndex(item => item.deleted) !== -1 &&
      status.push({
        key: GlobalPostsStatus.Deleted,
        value: GlobalPostsStatus.Deleted,
        color: colorCodes.find(item => item.key === GlobalPostsStatus.Deleted)?.color
      })

      setStatusData(status)
    }
    // get sorting options
    sorting.push(...Object.keys(FindGlobalPostsForPostTemplateSortMode).map(item => ({ key: item, value: item })))
    setSortData(sorting)
  }

  const loadData = (data: FindGlobalPostsForPostTemplateRequest) => {
    const {
      includeDeferredPosts,
      includePublishedPosts,
      networkType,
      postTemplateId,
      offset,
      pageSize,
      deletedPosts,
      deferredPostStatus,
      postSource,
      sortMode
    } = data
    dispatchFindGlobalPostsForPostTemplate({
      includeDeferredPosts,
      includePublishedPosts,
      deletedPosts,
      deferredPostStatus,
      postSource,
      networkType,
      postTemplateId,
      sortMode,
      offset,
      pageSize,
    })
  }

  const addOrReplace = (arr: any[], item: any) => {
    const index = arr.findIndex(obj => obj === item)
    index === -1 ? arr.push(item) : arr[index] = item
    return arr
  }

  const includePublishedHasTrue = (arr: DropDownElement[]) => {
    return arr.some(status =>
      status.key === GlobalPostsStatus.Successful ||
      status.key === GlobalPostsStatus.Individual ||
      status.key === GlobalPostsStatus.Deleted)
  }

  const includeDeferredHasTrue = (arr: DropDownElement[]) => {
    return arr.some(status =>
      status.key === GlobalPostsStatus.Failed ||
      status.key === GlobalPostsStatus.Pending ||
      status.key === GlobalPostsStatus.Individual ||
      status.key === GlobalPostsStatus.Manual ||
      status.key === GlobalPostsStatus.Notified ||
      status.key === GlobalPostsStatus.NoActiveUsersForPage)
  }

  const handleDataFiltering = () => {
    let requestParams: FindGlobalPostsForPostTemplateRequest = defaultValues
    const sortMode = filter.sorting?.key as FindGlobalPostsForPostTemplateSortMode

    if (filter.status && filter.status.length > 0) {
      filter.status.map(status => {
        switch (true) {
        case status.key === GlobalPostsStatus.Successful:
          if (filter.status) {
            requestParams = {
              ...requestParams,
              includePublishedPosts: true,
              includeDeferredPosts: includeDeferredHasTrue(filter.status),
              postSource: addOrReplace(requestParams.postSource ?? [], DeferredPostSource.AutomaticAbo),
              deferredPostStatus: [...requestParams.deferredPostStatus ?? []],
              sortMode,
            }
          }
          break
        case status.key === GlobalPostsStatus.Individual:
          if (filter.status) {
            requestParams = {
              ...requestParams,
              includePublishedPosts: true,
              includeDeferredPosts: includeDeferredHasTrue(filter.status),
              postSource: addOrReplace(requestParams.postSource ?? [], DeferredPostSource.IndividualPost),
              deferredPostStatus: [...requestParams.deferredPostStatus ?? []],
              sortMode,
            }
          }
          break
        case status.key === GlobalPostsStatus.Manual:
          if (filter.status) {
            requestParams = {
              ...requestParams,
              includePublishedPosts: true,
              includeDeferredPosts: includeDeferredHasTrue(filter.status),
              postSource: addOrReplace(requestParams.postSource ?? [], DeferredPostSource.ManualAbo),
              deferredPostStatus: [...requestParams.deferredPostStatus ?? []],
              sortMode,
            }
          }
          break
        case status.key === GlobalPostsStatus.Notified:
          if (filter.status) {
            requestParams = {
              ...requestParams,
              includeDeferredPosts: true,
              includePublishedPosts: includePublishedHasTrue(filter.status),
              postSource: addOrReplace(requestParams.postSource ?? [], DeferredPostSource.IndividualPost),
              deferredPostStatus: addOrReplace(requestParams.deferredPostStatus ?? [], DeferredPostStatus.AwaitingUserConfirmation),
              sortMode,
            }
          }
          break
        case status.key === GlobalPostsStatus.Pending:
          if (filter.status) {
            requestParams = {
              ...requestParams,
              includeDeferredPosts: true,
              includePublishedPosts: includePublishedHasTrue(filter.status),
              postSource: addOrReplace(addOrReplace(requestParams.postSource ?? [], DeferredPostSource.IndividualPost), DeferredPostSource.AutomaticAbo),
              deferredPostStatus: addOrReplace(requestParams.deferredPostStatus ?? [], DeferredPostStatus.Scheduled),
              sortMode,
            }
          }
          break
        case status.key === GlobalPostsStatus.Failed:
          if (filter.status) {
            requestParams = {
              ...requestParams,
              includeDeferredPosts: true,
              includePublishedPosts: includePublishedHasTrue(filter.status),
              postSource: [...requestParams.postSource ?? []],
              deferredPostStatus: addOrReplace(requestParams.deferredPostStatus ?? [], DeferredPostStatus.ErrorWhilePosting),
              sortMode,
            }
          }
          break
        case status.key === GlobalPostsStatus.NoActiveUsersForPage:
          if (filter.status) {
            requestParams = {
              ...requestParams,
              includeDeferredPosts: true,
              includePublishedPosts: includePublishedHasTrue(filter.status),
              postSource: [...requestParams.postSource ?? []],
              deferredPostStatus: addOrReplace(requestParams.deferredPostStatus ?? [], DeferredPostStatus.NoActiveUsersForPage),
              sortMode,
            }
          }
          break
        case status.key === GlobalPostsStatus.Deleted:
          if (filter.status) {
            requestParams = {
              ...requestParams,
              includeDeferredPosts: includeDeferredHasTrue(filter.status),
              includePublishedPosts: true,
              deletedPosts: true,
              postSource: [...requestParams.postSource ?? []],
              deferredPostStatus: [...requestParams.deferredPostStatus ?? []],
              sortMode,
            }
          }
          break
        }
      })
    } else {
      requestParams = { ...defaultValues, includeDeferredPosts: true, includePublishedPosts: true, sortMode }
    }
    loadData(requestParams)
  }

  const handleSetFilterOption = (option: DropDownElement | DropDownElement[], key: string) => {
    if (!Array.isArray(option) && option !== null) {
      setFilter({ ...filter, [key]: { key: option.key, value: option.value } })
    } else if (Array.isArray(option)) {
      setFilter({ ...filter, status: option })
    } else {
      setFilter({ ...filter, [key]: null })
    }
  }

  const handleColorMarkers = (data: FindGlobalPostsForPostTemplateResponse) => {
    const coloredArray: ColorKeysWithId[] = []
    // GlobalPostsStatus.Successful
    data.publishedPosts
      .filter(item => item.source === DeferredPostSource.AutomaticAbo)
      .map(item => coloredArray.push({ id: item.id, key: GlobalPostsStatus.Successful }))

    // GlobalPostsStatus.Individual
    data.publishedPosts
      .filter(item => item.source === DeferredPostSource.IndividualPost)
      .map(item => coloredArray.push({ id: item.id, key: GlobalPostsStatus.Individual }))

    data.deferredPosts
      .filter(item => item.source === DeferredPostSource.IndividualPost)
      .map(item => coloredArray.push({ id: item.id, key: GlobalPostsStatus.Individual }))

    // GlobalPostsStatus.Individual
    data.publishedPosts
      .filter(item => item.source === DeferredPostSource.ManualAbo)
      .map(item => coloredArray.push({ id: item.id, key: GlobalPostsStatus.Manual }))

    data.deferredPosts
      .filter(item => item.source === DeferredPostSource.ManualAbo)
      .map(item => coloredArray.push({ id: item.id, key: GlobalPostsStatus.Manual }))

    // GlobalPostsStatus.Notified
    data.deferredPosts
      .filter(item => item.source === DeferredPostSource.IndividualPost && item.status === DeferredPostStatus.AwaitingUserConfirmation)
      .map(item => coloredArray.push({ id: item.id, key: GlobalPostsStatus.Notified }))

    // GlobalPostsStatus.Pending
    data.deferredPosts
      .filter(item => item.status === DeferredPostStatus.Scheduled)
      .map(item => coloredArray.push({ id: item.id, key: GlobalPostsStatus.Pending }))

    data.deferredPosts
      .filter(item => item.status === DeferredPostStatus.Processing)
      .map(item => coloredArray.push({ id: item.id, key: GlobalPostsStatus.Pending }))

    // GlobalPostsStatus.Failed
    data.deferredPosts
      .filter(item => item.status === DeferredPostStatus.ErrorWhilePosting)
      .map(item => coloredArray.push({ id: item.id, key: GlobalPostsStatus.Failed }))

    // GlobalPostsStatus.NoActiveUsersForPage
    data.deferredPosts
      .filter(item => item.status === DeferredPostStatus.NoActiveUsersForPage)
      .map(item => coloredArray.push({ id: item.id, key: GlobalPostsStatus.NoActiveUsersForPage }))

    // GlobalPostsStatus.Deleted
    data.publishedPosts
      .filter(item => item.deleted)
      .map(item => coloredArray.push({ id: item.id, key: GlobalPostsStatus.Deleted }))
    setColoredArray(coloredArray)
  }

  useEffect(() => {
    handleDataFiltering()
  }, [])

  useEffect(() => {
    handleDataFiltering()
  }, [activeTab, filter, page])

  useEffect(() => {
    const overviewTabs: NetworkType[] = [NetworkType.Facebook]
    if (licensePermissions.instagram) {
      overviewTabs.push(NetworkType.Instagram)
    }
    if (licensePermissions.googleMyBusiness) {
      overviewTabs.push(NetworkType.GoogleMyBusiness)
    }
    setTabs(overviewTabs)
  }, [licensePermissions])

  useEffect(() => {
    if (globalPostsForTemplate) {
      extractFilterData(globalPostsForTemplate)
      handleColorMarkers(globalPostsForTemplate)
    }
  }, [globalPostsForTemplate])

  return (
    <Grid container direction="row">
      <Grid item xs={12}>
        <Box sx={{ mt: 2, mb: 0.5 }}>
          <ButtonComponent
            text={t('accountsCommon:backToOverview')}
            className={classes.button}
            startIcon={<KeyboardArrowLeftRounded/>}
            variant="text"
            color="info"
            onClick={() => history.push({
              pathname: '/editor/dashboard/networkContent',
              state: { detail: { templateId: id } },
            })}
            disableElevation
          />
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Paper sx={{ p: 4 }}>
          <Grid container item xs={12} spacing={2}>
            <Grid item xs={9}>
              {
                statusData &&
                <Autocomplete
                  multiple
                  size="small"
                  options={statusData}
                  getOptionLabel={(option) => t(`coreDashboard:${option.value}`)}
                  onChange={(e, value: any) => handleSetFilterOption(value, 'status')}
                  renderInput={(params) => (
                    <TextFieldComponent {...params} label={t('coreDashboard:statusOption')}/>
                  )}
                  renderTags={(value, getTagProps) => value.map((option, index) => (
                    <Chip
                      size="small"
                      style={{ backgroundColor: option.color }}
                      label={t<string>(`coreDashboard:${option.value}`)}
                      {...getTagProps({ index })}
                      key={index}
                    />
                  ))}
                  popupIcon={<KeyboardArrowDownIcon/>}
                />
              }
            </Grid>
            <Grid item xs={3}>
              {
                sortData &&
                <Autocomplete
                  size="small"
                  options={sortData}
                  value={filter.sorting}
                  getOptionLabel={(option) => t(`coreDashboard:${option.value}`)}
                  isOptionEqualToValue={(option: DropDownElement, value) => option.value === value.value}
                  onChange={(e, value: DropDownElement) => handleSetFilterOption(value, 'sorting')}
                  renderInput={(params) => (
                    <TextFieldComponent {...params} label={t('postingCommon:sortingOption')}/>
                  )}
                  popupIcon={<KeyboardArrowDownIcon/>}
                  disableClearable
                />
              }
            </Grid>
          </Grid>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <DashboardNetworkContentTabs
          activeItem={activeTab} tabs={tabs} onChangeTab={(tab: NetworkType) => {
            setActiveTab(tab)
          }}
        />
      </Grid>
      <Grid item xs={12}>
        {activeTab === NetworkType.Facebook &&
          <FacebookDetailsTab data={globalPostsForTemplate} colored={coloredArray}/>}
        {activeTab === NetworkType.Instagram &&
          <InstagramDetailsTab data={globalPostsForTemplate} colored={coloredArray}/>}
        {activeTab === NetworkType.GoogleMyBusiness &&
          // TODO GoogleMBDetailsTab should be created
          <NoDataComponent noDataText={t('networkContentMessages:noPostsYet')} alignment="center" margin="16px 0"/>}

      </Grid>
      <Grid item xs={12}>
        <Box component="div" style={{ display: 'flex', justifyContent: 'center' }}>
          {globalPostsForTemplate &&
            <Pagination
              totalItems={globalPostsForTemplate.total}
              itemsPerPage={pageSize}
              page={page}
              setPage={(page: number) => setPage(page)}
            />}
        </Box>
      </Grid>
    </Grid>
  )
}

export default EditorDashboardDetails
